/*
 * fw__js-anfrage-modal.js
 * ------------------------------------------------------------------
 * Geteiltes Anfrage-Popup-Modul. Wird auf der B2B-Collection-Seite UND
 * auf der Produktdetailseite (PDP) geladen. Das Markup liefert die Section
 * sections/fw_b2b_main-collection-products-form.liquid (#fwAnfrageModal),
 * die einmalig per fetch('/?sections=...') nachgeladen wird.
 *
 * Die Logik wurde 1:1 aus fw__js_b2b-collection.js ausgelagert; B2B- bzw.
 * PDP-spezifisches Verhalten steckt ausschliesslich in der config.
 *
 * Oeffentliche API:  window.fwAnfrageModal.open(config)
 *   config = {
 *     mode:        'b2b' | 'pdp' | 'collection',   // steuert Hinweis-Sichtbarkeit + Event-Namen
 *     subject:     String,          // readonly subject-Feld + mailto-Betreff
 *     title:       String,          // Modal-Ueberschrift
 *     getItems:    () => [item],    // Items fuer Anzeige + Versand (frisch beim Oeffnen)
 *     updateItem:  (variantId, patch) => void  // persistiert {qty} bzw. {offerAlternative} bzw. {productTitle} (Wunschartikel)
 *     removeItem:  (id) => void,    // optional: Remove-Button auf Wunschartikel-Karten (collection-Mode)
 *     onSuccess:   () => void,      // nach bestaetigtem Versand beim Schliessen
 *     showAddMore: Boolean,         // PDP: "weitere Artikel"-Hinweis einblenden
 *     onAddMore:   () => void       // PDP: Klick-Handler des Hinweises
 *   }
 * item-Format (kompatibel zur B2B-Anfrageliste):
 *   { variantId, sku, qty, step, productTitle, variantTitle, variantLabel,
 *     image, priceCents, isFree, offerAlternative }
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  const SECTION_ID = 'fw_b2b_main-collection-products-form';
  const SUBMIT_BTN_HTML = '<span class="fw-anf-submit-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg></span>Anfrage absenden';

  let _config          = null;
  let _sectionPromise  = null;
  let _initialized     = false;
  let _modalLocked     = false;
  let _successConfirmed = false;

  // ── Helfer (portiert aus fw__js_b2b-collection.js) ─────────────────────
  // Kombiniert Produktname + Varianten-Attribut(e) zu "Name, Groesse: 7".
  // Bei Wunschartikeln nur den (vom User editierten) Titel oder Fallback-Text.
  function buildDisplayName(item) {
    if (item.isFree) return (item.productTitle || '').trim() || '(Wunschartikel ohne Beschreibung)';
    const label = item.variantLabel || item.variantTitle || '';
    const baseName = item.productTitle || '—';
    const meaningfulVariant = label && !label.includes('Default Title');
    return meaningfulVariant ? `${baseName}, ${label}` : baseName;
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]));
  }

  // Generic dataLayer-Push fuer GTM.
  function track(eventName, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...(data || {}) });
  }

  // ── Section laden ──────────────────────────────────────────────────────
  // Laedt das Modal-Markup einmalig per Section-Render-API ans Body-Ende.
  async function loadSection() {
    if (document.getElementById('fwAnfrageModal')) { initModal(); return; }
    if (!_sectionPromise) {
      _sectionPromise = fetch('/?sections=' + SECTION_ID)
        .then(r => r.json())
        .then(json => {
          const html = json[SECTION_ID];
          if (html) {
            document.body.insertAdjacentHTML('beforeend', html);
            initModal();
          }
        })
        .catch(e => { _sectionPromise = null; console.error('Anfrage modal load error:', e); });
    }
    return _sectionPromise;
  }

  // ── Modal initialisieren (genau einmal) ────────────────────────────────
  function initModal() {
    if (_initialized) return;
    const modal = document.getElementById('fwAnfrageModal');
    if (!modal) return;
    _initialized = true;

    // Validity-Indikator: gruenes Haken fuer gueltig, rotes Kreuz fuer ungueltig.
    // Wrapper-Span ermoeglicht das ::after-Pseudoelement. "Touched"-State
    // (data-touched) verhindert das rote Kreuz, bevor der User im Feld war.
    function updateValidState(el, opts) {
      if (!el || !el.classList || !el.classList.contains('fw-anf-input')) return;
      if (opts && opts.markTouched) el.dataset.touched = 'true';

      let state = 'none';
      if (el.classList.contains('fw-anf-input--muted')) {
        state = 'none';
      } else if (el.tagName === 'SELECT') {
        state = el.value !== '' ? 'valid' : 'invalid';
      } else {
        const filled = (el.value || '').trim() !== '';
        const ok = el.checkValidity();
        if (ok && filled) state = 'valid';
        else if (!ok)     state = 'invalid';
        // empty optional → state bleibt 'none'
      }

      const touched = el.dataset.touched === 'true';
      let cssState = null;
      if (state === 'valid') cssState = 'is-valid';
      else if (state === 'invalid' && touched) cssState = 'is-invalid';

      el.classList.remove('is-valid', 'is-invalid');
      const wrap = el.parentElement && el.parentElement.classList.contains('fw-anf-input-wrap')
        ? el.parentElement : null;
      if (wrap) wrap.classList.remove('is-valid', 'is-invalid');

      if (cssState) {
        el.classList.add(cssState);
        if (wrap) wrap.classList.add(cssState);
      }
    }

    const anfForm = document.getElementById('fwAnfrageForm');
    if (anfForm) {
      // Inputs/Textareas in einen Wrapper-Span packen (selects + readonly ausgespart)
      anfForm.querySelectorAll('input.fw-anf-input:not(.fw-anf-input--muted), textarea.fw-anf-input').forEach(input => {
        if (input.parentElement && input.parentElement.classList.contains('fw-anf-input-wrap')) return;
        const wrap = document.createElement('span');
        wrap.className = 'fw-anf-input-wrap';
        if (input.tagName === 'TEXTAREA') wrap.classList.add('fw-anf-input-wrap--textarea');
        input.parentNode.insertBefore(wrap, input);
        wrap.appendChild(input);
      });
      anfForm.querySelectorAll('.fw-anf-input').forEach(el => updateValidState(el));
      anfForm.addEventListener('input',  e => updateValidState(e.target));
      anfForm.addEventListener('change', e => updateValidState(e.target));
      // Blur: Feld als "beruehrt" markieren → ungueltige Felder zeigen jetzt das rote Kreuz
      anfForm.addEventListener('focusout', e => updateValidState(e.target, { markTouched: true }));
    }

    // Schliesst das Modal. Bei bestaetigtem Erfolg wird der config.onSuccess-
    // Callback aufgerufen (B2B: Liste leeren + Panel-Sync, PDP: PDP-Liste leeren).
    function closeAnfrageModal() {
      if (_successConfirmed && _config && typeof _config.onSuccess === 'function') {
        _config.onSuccess();
      }
      _successConfirmed = false;
      modal.style.display = 'none';
      document.body.style.overflow = '';
      const status = document.getElementById('fwAnfrageStatus');
      if (status) { status.style.display = 'none'; status.innerHTML = ''; }
      const btn = document.getElementById('fwAnfrageSubmit');
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = SUBMIT_BTN_HTML;
      }
      const cancelBtn = document.getElementById('fwAnfrageCancel');
      if (cancelBtn) cancelBtn.style.display = '';
    }

    // Sperrt waehrend des Versands alle Schliesswege + Formularfelder.
    function lockModal() {
      _modalLocked = true;
      const closeBtn  = document.getElementById('fwAnfrageClose');
      const cancelBtn = document.getElementById('fwAnfrageCancel');
      if (closeBtn)  { closeBtn.disabled  = true; closeBtn.style.opacity  = '0.4'; closeBtn.style.cursor  = 'not-allowed'; }
      if (cancelBtn) { cancelBtn.disabled = true; cancelBtn.style.opacity = '0.5'; cancelBtn.style.cursor = 'not-allowed'; }
      const form = document.getElementById('fwAnfrageForm');
      if (form) form.querySelectorAll('input, select, textarea').forEach(el => el.disabled = true);
    }

    function unlockModal() {
      _modalLocked = false;
      const closeBtn  = document.getElementById('fwAnfrageClose');
      const cancelBtn = document.getElementById('fwAnfrageCancel');
      if (closeBtn)  { closeBtn.disabled  = false; closeBtn.style.opacity  = ''; closeBtn.style.cursor  = ''; }
      if (cancelBtn) { cancelBtn.disabled = false; cancelBtn.style.opacity = ''; cancelBtn.style.cursor = ''; }
      const form = document.getElementById('fwAnfrageForm');
      if (form) form.querySelectorAll('input, select, textarea').forEach(el => {
        if (!el.hasAttribute('readonly')) el.disabled = false;
      });
    }

    function closeIfUnlocked() { if (!_modalLocked) closeAnfrageModal(); }

    document.getElementById('fwAnfrageClose')?.addEventListener('click', closeIfUnlocked);
    document.getElementById('fwAnfrageCancel')?.addEventListener('click', closeIfUnlocked);
    document.getElementById('fwAnfrageBackdrop')?.addEventListener('click', closeIfUnlocked);
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && modal.style.display !== 'none' && !_modalLocked) closeAnfrageModal();
    });

    // PDP-Hinweis "weitere Artikel hinzufuegen" — Handler kommt aus der config.
    document.getElementById('fwAnfrageAddMore')?.addEventListener('click', function (e) {
      e.preventDefault();
      if (_config && typeof _config.onAddMore === 'function') _config.onAddMore();
    });

    document.getElementById('fwAnfrageSubmit')?.addEventListener('click', async function () {
      const form = document.getElementById('fwAnfrageForm');
      if (!form.checkValidity()) {
        // Alle Felder als "beruehrt" markieren → ungueltige zeigen jetzt das rote Kreuz
        form.querySelectorAll('.fw-anf-input').forEach(el => updateValidState(el, { markTouched: true }));
        form.reportValidity();
        return;
      }

      const sourceItems = (_config && typeof _config.getItems === 'function') ? (_config.getItems() || []) : [];

      // Primaere Validierung im collection-Mode (Wunschartikel-Beschreibung Pflicht).
      // Im B2B-Flow ist das Sicherheitsnetz — dort blockiert die Sidebar schon vorher.
      const emptyFree = sourceItems.find(it => it.isFree && !(it.productTitle || '').trim());
      if (emptyFree) {
        const status = document.getElementById('fwAnfrageStatus');
        if (status) {
          status.className = 'fw-anf-status fw-anf-status--error';
          status.innerHTML = '';
          const msg = document.createElement('div');
          msg.textContent = (_config && _config.mode === 'collection')
            ? 'Bitte geben Sie fuer jeden Wunschartikel eine Beschreibung ein.'
            : 'Mindestens ein Wunschartikel hat keine Beschreibung. Bitte schliessen Sie dieses Fenster und ergaenzen Sie die Beschreibung in der Anfrageliste.';
          status.appendChild(msg);
          status.style.display = '';
        }
        // Den ersten leeren Wunschartikel im Modal hervorheben und fokussieren
        const target = document.querySelector(`#fwAnfrageItemsList .fw-anf-wish-item__name--editable[data-free-id="${emptyFree.variantId}"]`);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          target.focus();
          target.style.boxShadow = '0 0 0 3px #ef4444';
          setTimeout(() => { target.style.boxShadow = ''; }, 2500);
        }
        return;
      }

      const items = sourceItems.map(item => {
        // Wunschartikel werden mit 1 EUR/Stk. bewertet, regulaer mit Variant-Preis (Cents → Euro)
        const isCustom = item.isFree === true;
        const priceCents = isCustom ? 100 : (parseInt(item.priceCents) || 0);
        return {
          product_id: isCustom ? item.variantId : (item.sku || String(item.variantId)),
          sku: item.sku || '',
          name: buildDisplayName(item),
          quantity: item.qty,
          offer_alternative: item.offerAlternative || false,
          is_custom: isCustom,
          price_cents: priceCents,
          price: priceCents / 100
        };
      });
      if (!items.length) return;

      const fd = new FormData(form);
      const street = (fd.get('street') || '').trim();
      const zip    = (fd.get('zip')    || '').trim();
      const city   = (fd.get('city')   || '').trim();

      // Ad-Click-IDs (Google/Microsoft) fuer Offline-Conversion-Tracking mitsenden.
      // Nur, wenn der User dem Ad-User-Data-Consent zugestimmt hat.
      let adGa = '', adMs = '', adCombined = '';
      if (typeof fwHasAdUserDataConsent === 'function' && fwHasAdUserDataConsent()) {
        adGa = localStorage.getItem('fw_ga_id') || '';
        adMs = localStorage.getItem('fw_ms_id') || '';
        const parts = [];
        if (adGa) parts.push(adGa);
        if (adMs) parts.push(adMs);
        adCombined = parts.join('|');
      }

      const payload = {
        salutation:       fd.get('salutation') || '',
        first_name:       (fd.get('first_name') || '').trim(),
        last_name:        (fd.get('last_name')  || '').trim(),
        email:            (fd.get('email')      || '').trim(),
        company:          (fd.get('company')    || '').trim(),
        phone:            (fd.get('phone')      || '').trim(),
        street,
        zip_code:         zip,
        city,
        shipping_address: [street, [zip, city].filter(Boolean).join(' ')].filter(Boolean).join(', '),
        subject:          (fd.get('subject')       || (_config && _config.subject) || 'Anfrage').trim(),
        delivery_type:    (fd.get('delivery_type') || '').trim(),
        message:          (fd.get('message')       || '').trim(),
        ga_click_id:      adGa,
        ms_click_id:      adMs,
        ad_click_ids:     adCombined,
        items
      };

      const btn = this;
      const status = document.getElementById('fwAnfrageStatus');
      btn.disabled = true;
      btn.textContent = 'Wird gesendet…';
      if (status) { status.style.display = 'none'; status.innerHTML = ''; }
      lockModal();

      // 1. App-Backend (primaer: speichert in DB + verschickt Mail server-seitig).
      //    appOk = HTTP ok UND App hat Mail erfolgreich verschickt. Sonst → Backup.
      let appOk = false, appResult = null;
      fwLog('[fwAnfrage] Submit → App | Payload:', payload);
      try {
        const res = await fetch('/apps/request-form/requests', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        fwLog('[fwAnfrage] Response status:', res.status, res.statusText);
        fwLog('[fwAnfrage] Response URL:', res.url);
        const rawText = await res.clone().text().catch(() => '<text() failed>');
        fwLog('[fwAnfrage] Response body (raw):', rawText);
        try {
          appResult = JSON.parse(rawText);
          fwLog('[fwAnfrage] Response body (parsed JSON):', appResult);
        } catch (parseErr) {
          fwLog('[fwAnfrage] Response is not valid JSON:', parseErr?.message);
          appResult = {};
        }
        appOk = res.ok && appResult?.mail_sent !== false;
        if (res.ok && appResult?.mail_sent === false) {
          fwLog('[fwAnfrage] App hat DB gespeichert (request_id=' + appResult?.request_id + '), aber Mail-Versand fehlgeschlagen → Backup wird aktiviert.');
        }
        fwLog('[fwAnfrage] appOk =', appOk, '| mail_sent =', appResult?.mail_sent, '| request_id =', appResult?.request_id);
      } catch (err) {
        fwLog('[fwAnfrage] Fetch zur App fehlgeschlagen:', err);
        appOk = false;
      }

      // value = Summe aller items.price * quantity (Wunschartikel = 1 EUR/Stk.).
      const totalCents = items.reduce((sum, it) => sum + (it.price_cents * it.quantity), 0);

      if (appOk) {
        // Erfolg: Bestaetigung anzeigen. Modal entsperren, Anfrageliste wird
        // ueber _successConfirmed in closeAnfrageModal via onSuccess geleert.
        _successConfirmed = true;

        // GTM-Conversion-Event: nur bei BACKEND-bestaetigtem Erfolg, getrennt
        // nach Modus, damit GTM PDP- und B2B-Anfragen separat auswerten kann.
        if (_config.mode === 'pdp') {
          const it0 = items[0] || {};
          track('pdp_request_form_submit', {
            subject: payload.subject,
            request_id: appResult?.request_id || null,
            variant_id: (sourceItems[0] && sourceItems[0].variantId) || null,
            sku: it0.sku || '',
            quantity: it0.quantity || 0,
            value: totalCents / 100,
            currency: 'EUR',
            customer_email: payload.email,
            customer_phone: payload.phone,
            customer_company: payload.company,
            is_logged_in: window.fwIsCustomer === true
          });
        } else if (_config.mode === 'collection') {
          track('collection_request_form_submit', {
            subject: payload.subject,
            request_id: appResult?.request_id || null,
            item_count: items.length,
            has_custom_items: items.some(it => it.is_custom),
            value: totalCents / 100,
            currency: 'EUR',
            items: items.map(it => ({
              item_id: it.sku || String(it.product_id),
              item_name: it.name,
              price: it.price,
              quantity: it.quantity,
              item_category: it.is_custom ? 'Wunschartikel' : 'Standard'
            })),
            customer_email: payload.email,
            customer_phone: payload.phone,
            customer_company: payload.company,
            is_logged_in: window.fwIsCustomer === true,
            has_ad_consent: typeof fwHasAdUserDataConsent === 'function' && fwHasAdUserDataConsent()
          });
        } else {
          track('b2b_request_form_submit', {
            request_id: appResult?.request_id || null,
            item_count: items.length,
            has_custom_items: items.some(it => it.is_custom),
            value: totalCents / 100,
            currency: 'EUR',
            items: items.map(it => ({
              item_id: it.sku || String(it.product_id),
              item_name: it.name,
              price: it.price,
              quantity: it.quantity,
              item_category: it.is_custom ? 'Wunschartikel' : 'Standard'
            })),
            customer_email: payload.email,
            customer_phone: payload.phone,
            customer_company: payload.company,
            is_logged_in: window.fwIsCustomer === true,
            has_ad_consent: typeof fwHasAdUserDataConsent === 'function' && fwHasAdUserDataConsent()
          });
        }

        btn.textContent = '✓ Gesendet';
        unlockModal();
        btn.disabled = true;  // Submit-Button bleibt visuell "gesendet"
        const cancelBtn = document.getElementById('fwAnfrageCancel');
        if (cancelBtn) cancelBtn.style.display = 'none';

        if (status) {
          status.className = 'fw-anf-status fw-anf-status--success';
          status.innerHTML = '';

          const wrap = document.createElement('div');
          wrap.style.cssText = 'text-align:center;padding:6px 4px;';

          const icon = document.createElement('div');
          icon.style.cssText = 'font-size:30px;line-height:1;margin-bottom:8px;';
          icon.textContent = '✓';
          wrap.appendChild(icon);

          const heading = document.createElement('div');
          heading.style.cssText = 'font-size:15px;font-weight:700;margin-bottom:10px;';
          heading.textContent = 'Vielen Dank für Ihre Anfrage!';
          wrap.appendChild(heading);

          const p1 = document.createElement('div');
          p1.style.cssText = 'font-size:13px;line-height:1.5;margin-bottom:8px;';
          p1.textContent = 'Wir haben Ihre Anfrage erhalten.';
          wrap.appendChild(p1);

          const p2 = document.createElement('div');
          p2.style.cssText = 'font-size:13px;line-height:1.5;margin-bottom:14px;';
          p2.textContent = 'Wir melden uns schnellstmöglich bei Ihnen, in der Regel innerhalb eines Werktages. Bei Rückfragen erreichen Sie uns gerne unter info@fasswulf.de.';
          wrap.appendChild(p2);

          const okBtn = document.createElement('button');
          okBtn.type = 'button';
          okBtn.className = 'btn btn--primary';
          okBtn.textContent = 'OK';
          okBtn.addEventListener('click', closeAnfrageModal);
          wrap.appendChild(okBtn);

          status.appendChild(wrap);
          status.style.display = '';
        }
        return;
      }

      // App nicht erreichbar oder Mail-Versand fehlgeschlagen → entsperren.
      unlockModal();

      // 2. Fallback: mailto:-Link mit vorausgefuellten Daten.
      const itemsText = items.map(i => {
        const prefix = i.is_custom ? '[Wunschartikel] ' : '';
        return `- ${prefix}${i.name}${i.sku ? ' (' + i.sku + ')' : ''}: ${i.quantity} Stk.${i.offer_alternative ? ' [Alternative erwünscht]' : ''}`;
      }).join('\n');

      const mailBody = [
        'Hallo Fasswulf-Team,',
        '',
        'ich möchte die folgende Anfrage stellen:',
        '',
        `Kontakt:   ${[payload.salutation, payload.first_name, payload.last_name].filter(Boolean).join(' ')}`,
        payload.company          ? `Firma:     ${payload.company}`          : '',
        `E-Mail:    ${payload.email}`,
        payload.phone            ? `Telefon:   ${payload.phone}`            : '',
        payload.shipping_address ? `Adresse:   ${payload.shipping_address}` : '',
        '',
        `Betreff:   ${payload.subject}`,
        payload.delivery_type    ? `Lieferart: ${payload.delivery_type}`    : '',
        payload.message          ? `\nNachricht:\n${payload.message}`       : '',
        '',
        'Angefragte Artikel:',
        itemsText,
        '',
        'Mit freundlichen Grüßen'
      ].filter(v => v !== '').join('\n');

      const mailtoUrl = 'mailto:info@fasswulf.de'
        + '?subject=' + encodeURIComponent((payload.subject || 'Anfrage') + ' über Onlineshop')
        + '&body='    + encodeURIComponent(mailBody);

      btn.disabled = false;
      btn.textContent = 'Erneut versuchen';
      if (status) {
        status.className = 'fw-anf-status fw-anf-status--warning';
        status.innerHTML = '';

        const text = document.createElement('div');
        text.style.marginBottom = '10px';
        text.textContent = 'Die Online-Übertragung ist gerade nicht möglich. Sie können die Anfrage stattdessen direkt per E-Mail an uns senden:';
        status.appendChild(text);

        const link = document.createElement('a');
        link.href = mailtoUrl;
        link.className = 'btn btn--primary';
        link.style.cssText = 'display:inline-flex;align-items:center;text-decoration:none;';
        link.innerHTML = '<span class="fw-anf-submit-icon"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,6 12,13 2,6"/></svg></span>Anfrage per E-Mail senden';
        status.appendChild(link);

        status.style.display = '';
      }
    });

    // ── Items-Liste editierbar: Mengen-Stepper + "Alternative anbieten" ──
    // Delegiert auf dem stabilen Container, da renderItems() das innerHTML bei
    // jedem open() neu baut. Persistenz über den config-Callback _config.updateItem.
    const itemsListEl = document.getElementById('fwAnfrageItemsList');
    if (itemsListEl) {
      itemsListEl.addEventListener('click', function (e) {
        // Remove-Button auf Wunschartikel-Karte (collection-Mode)
        const rm = e.target.closest('.fw-anf-wish-remove');
        if (rm) {
          if (_config && typeof _config.removeItem === 'function') {
            _config.removeItem(rm.dataset.variantId);
            renderItems();
          }
          return;
        }
        const btn = e.target.closest('.fw-anf-wish-qty__btn');
        if (!btn) return;
        const input = btn.closest('.fw-anf-wish-qty')?.querySelector('.fw-anf-wish-qty__input');
        if (!input) return;
        const step = parseInt(input.step) || 1;
        const min  = parseInt(input.min)  || 1;
        let val = parseInt(input.value) || min;
        val = btn.dataset.dir === 'minus' ? Math.max(min, val - step) : val + step;
        input.value = val;
        if (_config && typeof _config.updateItem === 'function') {
          _config.updateItem(btn.dataset.variantId, { qty: val });
        }
      });
      itemsListEl.addEventListener('change', function (e) {
        const t = e.target;
        if (t.classList && t.classList.contains('fw-anf-wish-qty__input')) {
          const val = Math.max(1, parseInt(t.value) || 1);
          t.value = val;  // normalisierten Wert ins DOM zurueckschreiben
          if (_config && typeof _config.updateItem === 'function') {
            _config.updateItem(t.dataset.variantId, { qty: val });
          }
        } else if (t.matches && t.matches('.fw-anf-wish-item__alt input[type="checkbox"]')) {
          if (_config && typeof _config.updateItem === 'function') {
            _config.updateItem(t.dataset.variantId, { offerAlternative: t.checked });
          }
        }
      });
    }
  }

  // ── config auf das Modal anwenden ──────────────────────────────────────
  function applyConfig() {
    // Titel: #fwAnfrageTitel enthaelt ein SVG-Icon + einen Textknoten.
    // Nur den (letzten) Textknoten ersetzen, damit das Icon erhalten bleibt.
    const titleEl = document.getElementById('fwAnfrageTitel');
    if (titleEl && _config.title) {
      let textNode = null;
      for (let i = titleEl.childNodes.length - 1; i >= 0; i--) {
        if (titleEl.childNodes[i].nodeType === Node.TEXT_NODE) { textNode = titleEl.childNodes[i]; break; }
      }
      if (textNode) textNode.textContent = ' ' + _config.title;
      else titleEl.appendChild(document.createTextNode(' ' + _config.title));
    }

    // Betreff-Feld (readonly) — value laesst sich trotz readonly setzen,
    // FormData liest readonly-Felder beim Versand mit.
    const subjectInput = document.querySelector('#fwAnfrageForm input[name="subject"]');
    if (subjectInput && _config.subject) subjectInput.value = _config.subject;

    // "Weitere Artikel"-Hinweis nur im PDP-Modus einblenden.
    const addMore = document.getElementById('fwAnfrageAddMore');
    if (addMore) addMore.style.display = _config.showAddMore ? '' : 'none';
  }

  // ── Items-Liste in der rechten Spalte rendern ──────────────────────────
  function renderItems() {
    const items = (_config && typeof _config.getItems === 'function') ? (_config.getItems() || []) : [];
    const listEl = document.getElementById('fwAnfrageItemsList');
    if (!listEl) return;
    if (!items.length) {
      listEl.innerHTML = '<span style="font-size:12px;color:#9ca3af">Keine Artikel in der Anfrageliste.</span>';
      return;
    }
    listEl.innerHTML = items.map(item => {
      const isFree = item.isFree;
      const vid  = escapeHtml(String(item.variantId));
      const qty  = parseInt(item.qty) || 1;
      const step = parseInt(item.step) || 1;
      const allowRemove = _config && typeof _config.removeItem === 'function';
      return `
        <div class="fw-anf-wish-item${isFree ? ' fw-anf-wish-item--free' : ''}">
          ${isFree
            ? `<div class="fw-anf-wish-item__no-img"><div class="fw-anf-wish-item__free-icon"><img src="https://cdn.shopify.com/s/files/1/0787/0513/1846/files/FW_Produktliste.svg?v=1768894840" alt="" loading="lazy"></div></div>`
            : (item.image
                ? `<div class="fw-anf-wish-item__img"><img src="${item.image}" alt="" width="40" height="40" loading="lazy"></div>`
                : `<div class="fw-anf-wish-item__no-img"></div>`)}
          <div class="fw-anf-wish-item__body">
            <div class="fw-anf-wish-item__header">
              ${isFree
                ? `<span class="fw-anf-wish-item__name fw-anf-wish-item__name--editable"
                          contenteditable="true"
                          data-placeholder="Beschreibung anklicken &amp; eingeben"
                          data-free-id="${vid}">${escapeHtml(item.productTitle || '')}</span>`
                : `<span class="fw-anf-wish-item__name">${escapeHtml(buildDisplayName(item))}</span>`}
              ${allowRemove ? `<button type="button" class="fw-anf-wish-remove" data-variant-id="${vid}" aria-label="Entfernen">×</button>` : ''}
            </div>
            ${isFree ? `<span class="fw-anf-wish-item__free-badge">Wunschartikel</span>` : ''}
            ${!isFree && item.sku ? `<span class="fw-anf-wish-item__sku">${escapeHtml(item.sku)}</span>` : ''}
            <div class="fw-anf-wish-item__footer">
              <div class="fw-anf-wish-qty">
                <button type="button" class="fw-anf-wish-qty__btn" data-dir="minus" data-variant-id="${vid}">−</button>
                <input type="number" class="fw-anf-wish-qty__input" value="${qty}" min="1" step="${step}" data-variant-id="${vid}">
                <button type="button" class="fw-anf-wish-qty__btn" data-dir="plus" data-variant-id="${vid}">+</button>
              </div>
              <label class="fw-anf-wish-item__alt">
                <input type="checkbox" data-variant-id="${vid}"${item.offerAlternative ? ' checked' : ''}>
                Alternative anbieten
              </label>
            </div>
          </div>
        </div>`;
    }).join('');

    // Click-to-edit fuer Wunschartikel-Titel im Modal (collection-Mode).
    // Live-Sync in den Storage via _config.updateItem({ productTitle }).
    listEl.querySelectorAll('.fw-anf-wish-item__name--editable').forEach(el => {
      el.addEventListener('input', function () {
        if (_config && typeof _config.updateItem === 'function') {
          _config.updateItem(this.dataset.freeId, { productTitle: this.textContent.trim() });
        }
        // Bei Tippen: Fehlermeldung ueber dem Submit-Button ausblenden, falls vorhanden.
        const status = document.getElementById('fwAnfrageStatus');
        if (status && status.classList.contains('fw-anf-status--error')) {
          status.style.display = 'none';
        }
      });
      el.addEventListener('paste', function (e) {
        e.preventDefault();
        const text = (e.clipboardData || window.clipboardData).getData('text');
        document.execCommand('insertText', false, text);
      });
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); this.blur(); }
      });
      // Save-Flash beim Verlassen — analog B2B-Sidebar: kurze gruene Bestaetigung.
      el.addEventListener('blur', function () {
        this.classList.remove('fw-name-saved');
        void this.offsetWidth;   // Browser zur Re-Animation zwingen
        this.classList.add('fw-name-saved');
        setTimeout(() => this.classList.remove('fw-name-saved'), 1300);
      });
    });
  }

  // ── Oeffentliche API ───────────────────────────────────────────────────
  async function openModal(config) {
    _config = config || {};
    await loadSection();
    const modal = document.getElementById('fwAnfrageModal');
    if (!modal) { console.error('fwAnfrageModal: Modal-Markup konnte nicht geladen werden.'); return; }

    applyConfig();
    renderItems();

    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    const items = (typeof _config.getItems === 'function') ? (_config.getItems() || []) : [];
    if (_config.mode === 'pdp') {
      const it = items[0] || {};
      track('pdp_request_form_view', {
        subject: _config.subject,
        variant_id: it.variantId || null,
        sku: it.sku || '',
        quantity: it.qty || 0
      });
    } else if (_config.mode === 'collection') {
      track('collection_request_form_view', {
        subject: _config.subject,
        item_count: items.length,
        has_custom_items: items.some(it => it.isFree)
      });
    } else {
      track('b2b_request_form_view', {
        item_count: items.length,
        has_custom_items: items.some(it => it.isFree)
      });
    }
  }

  window.fwAnfrageModal = { open: openModal };
})();
